import React from 'react';
import { Navigate, Outlet, useNavigate } from 'react-router-dom';
import { SkeletonHeader, SkeletonRegion, SkeletonTable } from '@parsina/framework';
import { ShieldAlert } from 'lucide-react';

import { useTranslation } from '../i18n';
import { GATE_ROLES, allows, homePath } from '../lib/access';
import SystemState from './SystemState';

/**
 * A route guard for one of the panel's two gates (`'platform' | 'crm'`).
 *
 * Used as a LAYOUT route around the routes a gate owns, so the routes
 * themselves stay one line each. Three outcomes:
 *
 *   access unknown  a neutral page skeleton, and — the point — the screen is
 *                   NOT mounted. A platform screen mounted for a CRM-only
 *                   user fires a query the BFF refuses with FORBIDDEN, and a
 *                   FORBIDDEN on the platform schema takes the whole panel
 *                   away (AuthGate). Waiting for `me` is what prevents that.
 *   allowed         the nested route.
 *   refused         the 403 `SystemState`, naming the missing role as its LTR
 *                   detail line — the same shape AuthGate uses for a caller
 *                   with no role at all — with a way to wherever this caller
 *                   CAN go.
 *
 * The server stays the authority on every request; this only decides what to
 * offer.
 */
export default function RequireAccess({ access, need, children }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!access) return <AccessPending />;

  if (!allows(access, need)) {
    const home = homePath(access);
    return (
      <SystemState
        tone="neutral"
        code="403"
        icon={<ShieldAlert size={30} aria-hidden />}
        titleKey="auth.routeDenied.title"
        adviceKey="auth.routeDenied.body"
        detail={GATE_ROLES[need]}
        testId={`route-denied-${need}`}
        actions={home ? [{
          key: 'home',
          label: home === '/dashboard' ? t('common.actions.goToDashboard') : t('common.actions.goToDemoRequests'),
          onClick: () => navigate(home),
        }] : []}
      />
    );
  }

  return children ?? <Outlet />;
}

/** While `me` resolves: the generic page frame, announced once. Never a spinner. */
export function AccessPending() {
  const { t } = useTranslation();
  return (
    <SkeletonRegion
      label={t('auth.resolving')}
      className="flex h-full flex-col gap-6 px-8 py-6"
      data-testid="access-pending"
    >
      <SkeletonHeader />
      <SkeletonTable rows={6} columns={5} dense />
    </SkeletonRegion>
  );
}

/** `/` for a caller with neither gate open: an honest page, not a 403 that blames their account. */
export function NoSections() {
  return (
    <SystemState
      tone="neutral"
      icon={<ShieldAlert size={30} aria-hidden />}
      titleKey="auth.noSections.title"
      adviceKey="auth.noSections.body"
      testId="route-no-sections"
    />
  );
}

/**
 * `/` lands where this caller can work: the dashboard for platform staff, the
 * demo-request queue for a CRM-only user (who cannot load the dashboard), and
 * an honest «nothing is open to you» for a caller the BFF admitted with
 * neither gate open. Nothing renders until `me` has answered.
 */
export function Home({ access }) {
  if (!access) return <AccessPending />;
  const home = homePath(access);
  return home ? <Navigate to={home} replace /> : <NoSections />;
}
